import React from 'react';
import { Container, Row, Col, Carousel } from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import { FiLock, FiClock, FiBriefcase, FiTrendingUp, FiArrowRight } from 'react-icons/fi';
import './GettingStarted.css';

// Images
import canvasImg1 from '../../assets/GettingStartedPictures/gettingStartedImg1.png';
import canvasImg2 from '../../assets/GettingStartedPictures/gettingStartedImg2.png';
import canvasImg3 from '../../assets/GettingStartedPictures/gettingStartedImg3.png';

const GettingStarted = () => {
    const { lang } = useLanguage();

    const content = {
        en: {
            heroTitle: "Let's Get Started",
            heroSubtitle: "We’ve rethought the learning process and built a proven system to help you develop the skills and knowledge you need to achieve your goals. Our programs are primarily delivered through in-person, instructor-led sessions, with flexible options available based on your needs. By the end of a course, you won’t just have gone through the material, you’ll have understood it, practiced it, and made it your own.To prepare you for real exams, we also provide practice tests designed to closely reflect actual exam conditions. Each question is carefully reviewed with the instructor, who explains the key concepts in detail.",
            card1Title: "Cybersecurity & Networking",
            card1Desc: "Get hands-on training in defensive and offensive cybersecurity, networking, systems, web technologies, and databases, and benefit from our CompTIA Partnership.",
            card2Title: "Convenient Learning",
            card2Desc: "Maintain your work or class schedule by studying and learning with convenient online courses that are always available using our Canvas Course Portal.",
            card3Title: "Workforce Ready",
            card3Desc: "Through immersive classes and a dynamic curriculum, you’ll develop the fundamental skills needed to be ready to use your knowledge in the workforce.",
            card4Title: "Career Success",
            card4Desc: "Benefit from a wide range of career services to help equip you for success as you work to change career paths or advance in your current position.",

            //Section 2
            canvasMainTitle: "Canvas: Our Course Portal",
            // Slide 1
            s1Badge: "Intuitive UI",
            s1Title: "Fluid Course Navigation",
            s1Desc: "Never feel lost with what is coming up next. Our interface is designed so you never feel lost, providing a clear roadmap of what’s completed and what’s coming up next.",
            s1List1: "Real-time progress tracking",
            s1List2: "One-click module access",
            // Slide 2
            s2Badge: "Global Standard",
            s2Title: "Canvas Easy Use Learning",
            s2Desc: "Lifelong learning and professional development for educators go hand-in-hand with Canvas Network's open, online courses, taught by experts and institutions worldwide.",
            s2List1: "Expert-led curriculum",
            s2List2: "Worldwide institution access",
            // Slide 3
            s3Badge: "Flexibility",
            s3Title: "Mobile Friendly Courses",
            s3Desc: "Have the convenience of being able to take canvas on the go. Optimized for every device, your classroom is always in your pocket and ready when you are.",
            s3List1: "Optimized for iOS & Android",
            s3List2: "Offline learning capabilities",

            // Section 3
            topicsTitle: "Topics We Teach"
        },
        jp: {
            heroTitle: "はじめましょう",
            heroSubtitle: "私たちは学び方そのものを見直し、目標達成に必要な知識とスキルを身につけられる、実績ある学習システムを作りました。授業は主に対面形式で行われ、講師が直接指導します。必要に応じて柔軟な受講方法にも対応しています。 コース修了時には、ただ内容を学ぶだけでなく、「理解し」「実践し」「自分の力として身につける」ことを目指します。 また、本番の試験に備えるため、実際の試験に近い形式の模擬試験も提供しています。問題は講師と一緒に丁寧に確認しながら、重要なポイントを詳しく解説します。",
            card1Title: "サイバーセキュリティネットワーキング",
            card1Desc: "サイバー攻撃からシステムを守る方法や、ネットワーク・Web・データベースの仕組みを実践的に学びます。CompTIAパートナーシップを通して、資格取得やキャリア形成もサポートします。",
            card2Title: "便利な学習環境",
            card2Desc: "仕事や学校の予定に合わせながら、いつでも利用できるCanvasコースポータルを通して、オンラインで学習できます。",
            card3Title: "即戦力を目指す学び",
            card3Desc: "実践的な授業と充実したカリキュラムを通して、学んだ知識を実際の仕事で活かせる基礎力を身につけます。",
            card4Title: "キャリアサポート",
            card4Desc: "キャリアチェンジや現在の職場でのステップアップに向けて、 それぞれの目標に向けた成長をサポートします。",

            // Section 2
            canvasMainTitle: "Canvas: 学習ポータル",
            // Slide 1
            s1Badge: "使いやすい画面設計",
            s1Title: "スムーズなコースナビゲーション",
            s1Desc: "コースの進行状況をひと目で確認できる設計になっています。完了した内容や次に進むべきステップを一目で確認できます。",
            s1List1: "学習の進捗をリアルタイムで確認",
            s1List2: "ワンクリックで各モジュールへアクセス可能",
            // Slide 2
            s2Badge: "グローバル基準",
            s2Title: "使いやすいCanvas学習",
            s2Desc: "教育者のための生涯学習と専門能力開発は、Canvas Networkのオープンなオンラインコースと密接に関連しています。世界中の専門家や機関によって提供されています。",
            s2List1: "専門家によるカリキュラム",
            s2List2: "世界中の教育機関へのアクセス",
            // Slide 3
            s3Badge: "柔軟性",
            s3Title: "モバイル対応コース",
            s3Desc: "外出先でもCanvasを利用できる利便性を提供します。あらゆるデバイスに最適化されているため、教室は常にあなたのポケットの中にあり、いつでも学習可能です。",
            s3List1: "iOSおよびAndroidに最適化",
            s3List2: "オフライン学習機能",

            // Section 3
            topicsTitle: "私たちが教えるトピック"
        }
    };

    const t = content[lang];

    return (
        <div className="getting-started-page">
            {/* Section 1 --------------------------------------------------------------- */}
            <section className="gs-hero-section py-5">
                <Container>
                    <div className="text-center mb-5 animate-text">
                        <h2 className="gs-main-title">{t.heroTitle}</h2>
                        <div className="small-red-line mx-auto"></div>
                        <p className="gs-subtitle mx-auto mt-4">
                            {t.heroSubtitle}
                        </p>
                    </div>

                    <Row className="g-4 justify-content-center">
                        <Col md={6} lg={5} className="animate-card">
                            <div className="gs-card h-100">
                                <div className="gs-icon-box"><FiLock size={35} /></div>
                                <h4>{t.card1Title}</h4>
                                <p>{t.card1Desc}</p>
                            </div>
                        </Col>

                        <Col md={6} lg={5} className="animate-card">
                            <div className="gs-card h-100">
                                <div className="gs-icon-box"><FiClock size={35} /></div>
                                <h4>{t.card2Title}</h4>
                                <p>{t.card2Desc}</p>
                            </div>
                        </Col>

                        <Col md={6} lg={5} className="animate-card">
                            <div className="gs-card h-100">
                                <div className="gs-icon-box"><FiBriefcase size={35} /></div>
                                <h4>{t.card3Title}</h4>
                                <p>{t.card3Desc}</p>
                            </div>
                        </Col>

                        <Col md={6} lg={5} className="animate-card">
                            <div className="gs-card h-100">
                                <div className="gs-icon-box"><FiTrendingUp size={35} /></div>
                                <h4>{t.card4Title}</h4>
                                <p>{t.card4Desc}</p>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
            {/* Section 1 --------------------------------------------------------------- */}

            {/* Section 2 - Canvas Our Course Portal */}
            <section className="canvas-section py-5">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="gs-main-title">{t.canvasMainTitle}</h2>
                        <div className="small-red-line mx-auto"></div>
                    </div>

                    <div className="canvas-slider-wrapper shadow-lg">
                        <Carousel fade indicators={true} controls={false} interval={5000} pause={false}>

                            {/* Slide 1 */}
                            <Carousel.Item>
                                <Row className="align-items-center g-0">
                                    <Col lg={6} className="p-0">
                                        <div className="slider-text-content p-5">
                                            <span className="badge-red mb-3">{t.s1Badge}</span>
                                            <h3>{t.s1Title}</h3>
                                            <p>{t.s1Desc}</p>
                                            <ul className="list-unstyled mt-4">
                                                <li><FiArrowRight className="text-red me-2" /> {t.s1List1}</li>
                                                <li><FiArrowRight className="text-red me-2" /> {t.s1List2}</li>
                                            </ul>
                                        </div>
                                    </Col>
                                    <Col lg={6} className="p-0">
                                        <div className="slider-img-container">
                                            <img src={canvasImg1} alt="Navigation" className="img-fluid slider-img-fixed" />
                                        </div>
                                    </Col>
                                </Row>
                            </Carousel.Item>

                            {/* Slide 2 */}
                            <Carousel.Item>
                                <Row className="align-items-center g-0">
                                    <Col lg={6} className="p-0">
                                        <div className="slider-text-content p-5">
                                            <span className="badge-red mb-3">{t.s2Badge}</span>
                                            <h3>{t.s2Title}</h3>
                                            <p>{t.s2Desc}</p>
                                            <ul className="list-unstyled mt-4">
                                                <li><FiArrowRight className="text-red me-2" /> {t.s2List1}</li>
                                                <li><FiArrowRight className="text-red me-2" /> {t.s2List2}</li>
                                            </ul>
                                        </div>
                                    </Col>
                                    <Col lg={6} className="p-0">
                                        <div className="slider-img-container">
                                            <img src={canvasImg2} alt="Learning" className="img-fluid slider-img-fixed" />
                                        </div>
                                    </Col>
                                </Row>
                            </Carousel.Item>

                            {/* Slide 3 */}
                            <Carousel.Item>
                                <Row className="align-items-center g-0">
                                    <Col lg={6} className="p-0">
                                        <div className="slider-text-content p-5">
                                            <span className="badge-red mb-3">{t.s3Badge}</span>
                                            <h3>{t.s3Title}</h3>
                                            <p>{t.s3Desc}</p>
                                            <ul className="list-unstyled mt-4">
                                                <li><FiArrowRight className="text-red me-2" /> {t.s3List1}</li>
                                                <li><FiArrowRight className="text-red me-2" /> {t.s3List2}</li>
                                            </ul>
                                        </div>
                                    </Col>
                                    <Col lg={6} className="p-0">
                                        <div className="slider-img-container">
                                            <img src={canvasImg3} alt="Mobile" className="img-fluid slider-img-fixed" />
                                        </div>
                                    </Col>
                                </Row>
                            </Carousel.Item>

                        </Carousel>
                    </div>
                </Container>
            </section>

            {/* Section 2 --------------------------------------------------------------- */}

            {/* Section 3 - (Topics We Teach)--------------------------------------------------------------- */}

            <section className="topics-section py-5">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="gs-main-title">{t.topicsTitle}</h2>
                        <div className="small-red-line mx-auto"></div>
                    </div>

                    <Row className="g-4 justify-content-center">
                        {[
                            "HTML", "CSS", "Design", "JavaScript", "Ruby", "PHP",
                            "Android", "C#", "Databases", "Development Tools", "Business", "Python",
                            "Java", "Digital Literacy", "EDI", "21st Century Skills",
                            "Data Analysis", "APIs", "Security", "Go", "Quality Assurance",
                            "Machine Learning", "Learning Resources", "Computer Science"
                        ].map((topic, index) => (
                            <Col key={index} xs={6} md={4} lg={3}>
                                <div className="topic-card animate-card">
                                    <div className="topic-dot"></div>
                                    <span>{topic}</span>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Section 3 --------------------------------------------------------------- */}

        </div>
    );
};

export default GettingStarted;